import React, { useEffect, useMemo, useState } from "react";
import "./../../../styles/SupportDashboard.css";
import Button from './../../ui/Button';
import { useAuth } from "../../../context/AuthContext";
import { UserType } from './../../../constants/userType';

export default function CreateUserModal({ isOpen, onClose, sites = [], onSubmit }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [type, setType] = useState("");
  const [groupQuery, setGroupQuery] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState({});
  const [userPlatform, setUserPlatform] = useState("mobile");
  const [msifile, setMsifile] = useState("");
  const [macpack, setMacPack] = useState("");
  const [chromeurl, setChromeUrl] = useState("");
  const { userType } = useAuth();
  const isSuperUser = userType === UserType.SUPER;
  const [newGroupId, setNewGroupId] = useState("");
  const [newGroupQuery, setNewGroupQuery] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setUsername("");
    setPassword("");
    setSelectedLocations([]);
    setType("");
    setGroupQuery("");
    setShowPw(false);
    setSaving(false);
    setErr({});
    setEmail("");
    setUserPlatform("mobile");
    setMsifile("");
    setMacPack("");
    setChromeUrl("");
    setNewGroupId("");
    setNewGroupQuery("");
  }, [isOpen]);

  const siteOptions = useMemo(() => {
    const normalized = (sites || [])
      .map((s) => ({
        label: s.sitename,
        value: String(s.id),
        level: s.level || "",
        parentId: s.parentId
      }))
      .filter((s) => s.value !== "1"); 

    const q = groupQuery.trim().toLowerCase();
    const filtered = !q
      ? normalized
      : normalized.filter((x) =>
          `${x.label} ${x.level} ${x.value}`.toLowerCase().includes(q)
        );

    const limit = q ? 50 : 200;
    return filtered.slice(0, limit);
  }, [sites, groupQuery]);

  const selectedText = useMemo(() => {
    return selectedLocations.join(",");
  }, [selectedLocations]);

  const toggleLocation = (id) => {
    setSelectedLocations((prev) => {
      const exists = prev.includes(id);
      if (exists) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const clearLocations = () => {
    setSelectedLocations([]);
  };

  const validate = () => {
    const e = {};
    if (!username.trim()) e.username = "Username is required.";
    else if (username.trim().length < 3) e.username = "Username looks too short.";

    if (!password) e.password = "Password is required.";
    else if (password.length < 12) e.password = "Use at least 12 characters.";

    if (!email.trim()) e.email = "Email is required.";

    if (userPlatform === "mobile") {
      if (type.length === 0) e.type = "User type is required.";
      if (selectedLocations.length === 0) e.location = "Select at least one site.";
    } else if (userPlatform === "pc") {
      if (!newGroupId) e.newGroupId = "Select an Android Group.";
      if (!msifile.trim()) e.msifile = "MSI File URL is required.";
      if (!macpack.trim()) e.macpack = "Mac Package URL is required.";
      if (!chromeurl.trim()) e.chromeurl = "Chrome URL is required.";
    }

    setErr(e);
    return Object.keys(e).length === 0;
  };
function extractApiError(ex) {

  const data = ex?.response?.data;
  const payload = data ?? ex?.data ?? ex?.body ?? null;
  const errors = payload?.errors;
  const message =
    payload?.message ||
    (Array.isArray(errors) && errors[0]?.message) ||
    ex?.message ||
    "Request failed.";

  return { payload, message, errors: Array.isArray(errors) ? errors : [] };
}

const canSubmit = useMemo(() => {
  if (!username.trim() || !password || !userPlatform || !email.trim() || saving) {
    return false;
  }
  
  if (userPlatform === "mobile") {
    return !!type && selectedLocations.length > 0;
  } else if (userPlatform === "pc") {
    return !!newGroupId && !!msifile.trim() && !!macpack.trim() && !!chromeurl.trim();
  }
  
  return false;
}, [username, password, selectedLocations, type, userPlatform, email, saving, newGroupId, msifile, macpack, chromeurl]);

  const buildUserPayload = () => {
    const basePayload = {
      username: username.trim(),
      password,
      email: email.trim(),
    };

    if (userPlatform === "mobile") {
      return {
        ...basePayload,
        type: type.trim().toLowerCase(),
        location: selectedLocations.join(","),
      };
    } else if (userPlatform === "pc") {
      return {
        ...basePayload,
        groupId: newGroupId,
        msifile: msifile.trim(),
        macpack: macpack.trim(),
        chromeurl: chromeurl.trim(),
      };
    }

    return basePayload;
  };

  const submit = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = buildUserPayload();
      await onSubmit({
        ...payload,
        platform: userPlatform,
      });
      onClose();
    } catch (ex) {
      const { message, errors } = extractApiError(ex);

      const fieldErrors = {};
      const messages = [];

      for (const e of errors) {
        if (!e?.message) continue;
        messages.push(e.message);

        if (e.target) {
          fieldErrors[e.target] = e.message;
          continue;
        }

        const msg = e.message.toLowerCase();
        if (msg.includes("username")) fieldErrors.username = e.message;
        if (msg.includes("password")) fieldErrors.password = e.message;
        if (msg.includes("email")) fieldErrors.email = e.message;
        if (msg.includes("location") || msg.includes("group")) fieldErrors.location = e.message;
        if (msg.includes("type")) fieldErrors.type = e.message;
      }

      setErr((prev) => ({
        ...prev,
        ...fieldErrors,
        api: messages.length ? messages.join(" • ") : message,
      }));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="sd-modal-backdrop" role="dialog" aria-modal="true">
      <div className="sd-modal sd-modal-md">
        <div className="sd-modal-header">
          <div>
            <h2 className="sd-title">Create user</h2>
            <div className="sd-subtitle">Create a new account and assign one or more sites.</div>
          </div>
          <button className="sd-icon-btn" onClick={onClose} aria-label="Close" type="button">
            ✕
          </button>
        </div>

        <div className="sd-modal-body">
          {err.api && <div className="sd-alert sd-alert-bad">{err.api}</div>}

          <div className="sd-field">
            <div className="sd-label">User Platform</div>
            <div className="sd-seg" role="tablist">
              <button 
                type="button"
                role="tab"
                aria-selected={userPlatform === "mobile"}
                aria-controls="mobile-panel"
                className={`sd-seg-btn ${userPlatform === "mobile" ? "is-active" : ""}`} 
                onClick={() => {
                  setUserPlatform("mobile");
                  setSelectedLocations([]);
                  setNewGroupId("");
                  setNewGroupQuery("");
                  setGroupQuery("");
                }}
              >
                Mobile
              </button>
              <button 
                type="button"
                role="tab"
                aria-selected={userPlatform === "pc"}
                aria-controls="pc-panel"
                className={`sd-seg-btn ${userPlatform === "pc" ? "is-active" : ""}`} 
                onClick={() => {
                  setUserPlatform("pc");
                  setSelectedLocations([]);
                  setNewGroupId("");
                  setNewGroupQuery("");
                  setGroupQuery("");
                }}
              >
                Install Portal Account
              </button>
            </div>
            {err.platform && <div className="sd-error">{err.platform}</div>}
          </div>

          <div className="sd-field">
            <div className="sd-label">Username</div>
            <input
              className={`sd-input ${err.username ? "has-error" : ""}`}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="e.g. ME040012895-2"
              autoComplete="off"
            />
            {err.username && <div className="sd-error">{err.username}</div>}
          </div>

          <div className="sd-field">
            <div className="sd-label">Password</div>
            <div className="sd-inline">
              <input
                className={`sd-input ${err.password ? "has-error" : ""}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPw ? "text" : "password"}
                placeholder="Enter a strong password…"
                autoComplete="new-password"
              />
              <Button type="button" buttonStyle="btn-alternate" onClick={() => setShowPw((v) => !v)}>
                {showPw ? "Hide" : "Show"}
              </Button>
            </div>
            {err.password && <div className="sd-error">{err.password}</div>}
            <div className="sd-help">(NCSC) recommends that the best passwords are made up of three random words</div>           
          </div>

          <div className="sd-field">
            <div className="sd-label">Email</div>
            <input
              className={`sd-input ${err.email ? "has-error" : ""}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. user@example.com"
              autoComplete="off"
            />
            {err.email && <div className="sd-error">{err.email}</div>}
          </div>
          <div className="sd-divider" />

          {userPlatform === "mobile" && (
          <>
          <div className="sd-field">
            <div className="sd-label">Select user type</div>
            <div className="sd-seg">
              <button type="button" className={`sd-seg-btn ${type === "police" ? "is-active" : ""}`} onClick={() => setType("police")}>
                Police
              </button>
              <button type="button" className={`sd-seg-btn ${type === "teamleader" ? "is-active" : ""}`} onClick={() => setType("teamleader")}>
                Team Leader
              </button>
              <button type="button" className={`sd-seg-btn ${type === "analyst" ? "is-active" : ""}`} onClick={() => setType("analyst")}>
                Analyst
              </button>
              <button type="button" className={`sd-seg-btn ${type === "support" ? "is-active" : ""}`} onClick={() => setType("support")}>
                Support
              </button>
              {isSuperUser && (
                <button type="button" className={`sd-seg-btn ${type === "super" ? "is-active" : ""}`} onClick={() => setType("super")}>
                  Super
                </button>
              )}
            </div>
            {err.type && <div className="sd-error">{err.type}</div>}
          </div>

          <br/>

          <div className="sd-field">
            <div className="sd-label">Assign sites</div>
            <div className="sd-field"></div>
              <input
                className="sd-input"
                value={groupQuery}
                onChange={(e) => setGroupQuery(e.target.value)}
                placeholder="Search sites…"
                autoComplete="off"
              />
              <Button type="button" buttonStyle="btn-alternate" onClick={clearLocations} disabled={selectedLocations.length === 0}>
                Clear Selected Locations
              </Button>
          </div>

          <div className="sd-help">
              Selected IDs: <strong>{selectedText || "None"}</strong>
          </div>
            {err.location && <div className="sd-error">{err.location}</div>}

          <div className="sd-dropdown">
              {siteOptions.length === 0 ? (
                <div className="sd-dropdown-empty">No matches</div>
              ) : (
                siteOptions.map((s) => {
                  const isSel = selectedLocations.includes(s.value);
                  return (
                    <button
                      key={s.value}
                      type="button"
                      className={`sd-dd-item ${isSel ? "is-selected" : ""}`}
                      onClick={() => toggleLocation(s.value)}
                      aria-pressed={isSel}
                    >
                      <span className="sd-dd-label">
                        <span className={`sd-dd-check ${isSel ? "is-on" : ""}`}>{isSel ? "✓" : ""}</span>
                        {s.label}
                      </span>
                      <span className="sd-dd-meta">{s.level ? s.level : `#${s.value}`}</span>
                    </button>
                  );
                })
              )}
            </div>   

          </>
          )} 

       {userPlatform === "pc" && (
          <>

          <div className="sd-field">
            <div className="sd-label">Assign Android Group</div>
            <div className="sd-field">
              <input
              className="sd-input"
              value={newGroupQuery}
              onChange={(e) => {
              setNewGroupQuery(e.target.value);
              if (newGroupId) setNewGroupId(null); 
            }}
            placeholder="Search destination group…"
            autoComplete="off"
              />

              <div className="sd-dropdown">
                {siteOptions.length === 0 ? (
                  <div className="sd-dropdown-empty">No matches</div>
                ) : (
                  siteOptions
                  .filter(s => s.label.toLowerCase().includes(newGroupQuery.toLowerCase()))
                  .map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      className={`sd-dd-item ${String(newGroupQuery) === String(s.value) ? "is-selected" : ""}`}
                      onClick={() => {
                        setNewGroupId(s.value);
                        setNewGroupQuery(s.label);
                      }}
                    >
                      <span className="sd-dd-label">{s.label}</span>
                      <span className="sd-dd-meta">{s.level ? s.level : `#${s.value}`}</span>
                    </button>
                  ))
                )}
              </div>
              {err.newGroupId && <div className="sd-error">{err.newGroupId}</div>}
            </div>
          </div>

          <div className="sd-field">
            <div className="sd-label">MSI File</div>
            <input
              className={`sd-input ${err.msifile ? "has-error" : ""}`}
              value={msifile}
              onChange={(e) => setMsifile(e.target.value)}
              placeholder="https://vm14.esafeglobal.com/msi/SITE-NAME/eSafeInstaller.exe"
              autoComplete="off"
            />
            {err.msifile && <div className="sd-error">{err.msifile}</div>}
          </div>
          
          <div className="sd-field">
            <div className="sd-label">Mac Pac</div>
            <input
              className={`sd-input ${err.macpack ? "has-error" : ""}`}
              value={macpack}
              onChange={(e) => setMacPack(e.target.value)}
              placeholder="https://vm14.esafeglobal.com/MACs/SITE-NAME/macos.mpkg.zip"
              autoComplete="off"
            />
            {err.macpack && <div className="sd-error">{err.macpack}</div>}
          </div>
 
          <div className="sd-field">
            <div className="sd-label">Chrome URL</div>
            <input
              className={`sd-input ${err.chromeurl ? "has-error" : ""}`}
              value={chromeurl}
              onChange={(e) => setChromeUrl(e.target.value)}
              placeholder="https://vm25.esafeglobal.com/SITE-NAME/SITEID"
              autoComplete="off"
            />
            {err.chromeurl && <div className="sd-error">{err.chromeurl}</div>}
          </div>
          </>
          )}
        </div>

        <div className="sd-modal-footer">
          <Button onClick={onClose} buttonStyle="btn-alternate" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} buttonStyle="btn-main" disabled={!canSubmit}>
            {saving ? "Creating…" : "Create user"}
          </Button>
        </div>
      </div>
    </div>
  );
}
